/**
 * Shared tab identifiers for the Group Dashboard (`/settings/groups/:id/details`).
 *
 * Extracted into its own tiny module so presentational pieces (e.g.
 * `parts/GroupTabBar.tsx`) and the page shell (`GroupDetail.tsx`) can both
 * reference the same `TabId` union without a circular import between the
 * shell and its children. `GroupDetail.tsx` is migrated onto this module in a
 * later task; until then both definitions stay byte-for-byte in sync.
 */

export type TabId =
  | 'overview'
  | 'students'
  | 'schedule'
  | 'zoom'
  | 'pandadoc'
  | 'materials'
  | 'communication'
  | 'tasks'
  | 'finances';

/** Canonical left-to-right tab order rendered by the tab bar. */
export const TAB_IDS: TabId[] = [
  'overview',
  'students',
  'schedule',
  'zoom',
  'pandadoc',
  'materials',
  'communication',
  'tasks',
  'finances',
];

/** Narrowing guard for an untrusted string (e.g. a `?tab=` URL value). */
export const isTabId = (value: string | null): value is TabId =>
  value !== null && (TAB_IDS as string[]).includes(value);

/** DOM id for a tab control, used as the `aria-controls` target's pair. */
export const tabElementId = (tab: TabId): string => `group-tab-${tab}`;

/** DOM id for the panel a tab controls (`role="tabpanel"` owner). */
export const tabPanelId = (tab: TabId): string => `group-tabpanel-${tab}`;
